import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoleAccessModal from '../../Modals/editRoleAccessModal';
import { useUpdateUsersMutation } from '../../services/user';
import { showError, showToast } from '../../constants/toasts';
import { PROFILE_STATUS, PROFILE_STATUS_NAME } from '../../utils/enums';

interface LinkedAccount {
  id: number;
  created_by: number;
  shared_user: {
    id: number;
    email: string;
    phone_number: string;
    first_name: string;
    last_name: string;
    profile_picture?: {
      id: number;
      media_file_name: string;
      file_type: string;
      thumbnail: string | null;
      media_file_url: string;
      audio_pitch: any[];
    } | null;
    profile_status: number;
  };
  assigned_role: number;
  assigned_role_label: string;
  shared_goal: boolean;
  shared_budget: boolean;
  shared_calendar: boolean;
}

interface LinkedAccountsTableProps {
  linkedAccounts?: LinkedAccount[];
  onRoleUpdate?: () => void; // Callback to refresh data after role update
}

const LinkedAccountsTable: React.FC<LinkedAccountsTableProps> = ({
  linkedAccounts = [],
  onRoleUpdate
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [editRoleOpen, setEditRoleOpen] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [updatingRoleId, setUpdatingRoleId] = useState<number | null>(null);
  const [userUpdate] = useUpdateUsersMutation();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, rowId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleRoleUpdate = async (accountId: number, newRole: number) => {
    try {
      setLoading(true);
      setUpdatingRoleId(accountId);
      const body = {
        assigned_role: newRole
      };

      const response = await userUpdate({ body, id: accountId }).unwrap();

      if (response?.status === 200) {
        showToast('Role updated successfully');

        if (onRoleUpdate) {
          onRoleUpdate();
        }
      } else {
        showError('Failed to update role');
      }
    } catch (error: any) {
      showError(error?.data?.message || 'Failed to update role');
    } finally {
      setLoading(false);
      setUpdatingRoleId(null);
    }
  };

  const getStatusFromProfileStatus = (profileStatus: number) => {
    return PROFILE_STATUS_NAME[profileStatus] || 'Unknown';
  };

  const getStatusClass = (profileStatus: number) => {
    switch (profileStatus) {
      case PROFILE_STATUS.PROFILE_COMPLETED:
        return 'c_success';
      case PROFILE_STATUS.OTP_VERIFIED:
        return 'c_warning';
      case PROFILE_STATUS.ONLY_SIGNUP:
      default:
        return 'c_danger';
    }
  };

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Linked User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linkedAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  No linked accounts found
                </TableCell>
              </TableRow>
            ) : (
              linkedAccounts.map((account) => (
                <TableRow hover key={account.id}>
                  <TableCell>
                    <div className="user_block">
                      <figure>
                        <img
                          src={
                            account.shared_user.profile_picture?.media_file_url ||
                            "/static/images/user_placeholder.png"
                          }
                          alt={account.shared_user.first_name}
                        />
                      </figure>
                      <p>
                        <b>{`${account.shared_user.first_name} ${account.shared_user.last_name}`}</b>
                        <span>{account.shared_user.email}</span>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="form" style={{ position: 'relative' }}>
                      <Select
                        fullWidth
                        value={account.assigned_role}
                        onChange={(e) => {
                          const newRole = Number(e.target.value);
                          if (newRole !== account.assigned_role) {
                            handleRoleUpdate(account.id, newRole);
                          }
                        }}
                        disabled={updatingRoleId === account.id}
                      >
                        <MenuItem value={1}>Owner</MenuItem>
                        <MenuItem value={2}>Partner</MenuItem>
                        <MenuItem value={3}>Contributor</MenuItem>
                        <MenuItem value={4}>Viewer</MenuItem>
                      </Select>
                      {updatingRoleId === account.id && (
                        <CircularProgress
                          size={20}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`status_btn ${getStatusClass(account.shared_user.profile_status)}`}>
                      {getStatusFromProfileStatus(account.shared_user.profile_status)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <Box>
                        <IconButton
                          aria-controls={open && selectedRowId === account.id ? 'linked-menu' : undefined}
                          aria-haspopup="true"
                          aria-expanded={open && selectedRowId === account.id ? 'true' : undefined}
                          onClick={(e) => handleClick(e, account.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          id="basic-menu"
                          anchorEl={anchorEl}
                          open={open && selectedRowId === account.id}
                          onClose={handleClose}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <MenuItem onClick={handleClose}>Merge / Split Account</MenuItem>
                          <MenuItem onClick={handleClose}>Review Shared Goals/Budgets</MenuItem>
                          <MenuItem onClick={() => { handleClose(); setEditRoleOpen(true) }}>Edit Permissions</MenuItem>
                          <MenuItem onClick={handleClose}>Force Unlink</MenuItem>
                        </Menu>
                      </Box>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditRoleAccessModal
        open={editRoleOpen}
        onClose={() => setEditRoleOpen(false)}
        setOpen={setEditRoleOpen}
        id={selectedRowId}
      />
    </>
  );
};

export default LinkedAccountsTable;